import { useMutation, useQuery } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, Table, useReactTable } from '@tanstack/react-table';
import { formatDistanceToNow } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from 'styled-components';
import { useObserver } from '../../../../CodesThatsGottaGoSomewhereElse';
import { ApiClient } from '../../../../api/ApiClient';
import { v2 } from '../../../../api/ApiInterfaceV2';
import { DirectInvitationData, EntityWithLabel } from '../../../../api/ApiTypes';
import { Icon } from '../../../../concepts/resources/Icon';
import { Colors, Input, Menu, remInPx, Toggle } from '../../../../ui';
import { ButtonToggle } from '../../../../ui/ButtonToggle';
import { Checkbox } from '../../../../ui/Checkbox';
import { LoadingGuard } from '../../../../ui/LoadingGuard';
import { DropMenuButton, MenuButton } from '../../../../ui/MenuButton';
import { PageButtonStrip } from '../../../../ui/PageButtonStrip';
import { Tip } from '../../../../ui/Tip';
import { AppEvents } from '../../../AppEvents';
import { useResolve } from '../../../Container';
import { AccountModel } from '../../../account/AccountModel';
import { DataTable } from '../../../common/DataTable';
import { Copy, DisableFrame, Flexer, Title, ToggleLabel } from '../../../dashboard/elements/LayoutElements';
import { memberInvitePopup } from '../../../dashboard/members/MemberInvitePopup';
import { PopupManager } from '../../../popups/PopupManager';
import { UserInfoButton } from '../../shared/UserInfoButton';
import { WorkspaceInfoButton } from '../../shared/WorkspaceInfoButton';
import { useOnBulkApproveDirectInvites, useOnBulkRejectDirectInvites } from '../AdminActions';
import { StatsGroup, StatsGroupDivider } from '../ManageElements';
import { InviteStateCell, TextCell } from '../common/TableElements';
import { TabProps } from './ManagePeopleView';
import { UserStatsView } from './PeopleMembersListView';
import { WorkspaceSelectPopup } from './WorkspaceSelectPopup';

const InviteIcon = styled(Icon)`
    width: 1.875rem;
    height: 1.875rem;
    border-radius: .25rem;
`;

const InviteButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: .25rem;
    cursor: pointer;
    color: ${Colors.text};
 
    &:hover {
        color: ${Colors.primary};
        ${InviteIcon} {
            background: rgba(59, 101, 239, 0.10);
        }
    }
    &:active {
        color: ${Colors.primary};
        ${InviteIcon} {
            background: rgba(59, 101, 239, 0.20);
        }
    }
`;

const PendingBlock = styled.div`
    position: absolute;
    top: -21%;
    right: -7%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 1.5rem;
    background-color: ${Colors.observe};
    color: ${Colors.white};
    font-weight: 500;
    padding: 0 .5rem;
`;

const ApprovalTipIndicator = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${Colors.textMenu};
    background-color: ${Colors.border};
    height: 2rem;
    width: 2rem;
    border-radius: 1.5rem;
    border: 1px solid ${Colors.border};
    cursor: pointer;
`;

const ApprovalTipText = styled.div`
    position: absolute;
    bottom: 0;
    right: 0;
    transform: translate(0%, 105%);
    display: flex;
    flex-direction: column;
    gap: .75rem;
    width: 15rem;
    color: ${Colors.textMenu};
    background-color: #fff;
    padding: 1rem;
    border-radius: .5rem;
    font-weight: 500;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
    z-index: 1000;
`;

const ActionsCell = ({ value }: { value: DirectInvitationData }) => {
    const [open, setOpen] = useState(false);
    const setClosed = useCallback(() => setOpen(false), []);

    const onBulkApproveDirectInvites = useOnBulkApproveDirectInvites([value.id], setClosed);
    const onBulkRejectDirectInvites = useOnBulkRejectDirectInvites([value.id], setClosed);

    return <Flexer flex="row">
        <DropMenuButton icon="deqo://icons/hero/dots-horizontal" open={open} setOpen={setOpen}>
            <Flexer minWidth="10">
                <Menu>
                    {value.state === 'pending' && <Menu.Item onClick={onBulkApproveDirectInvites}>
                        <Menu.ItemText>
                            Approve
                        </Menu.ItemText>
                    </Menu.Item>}
                    <Menu.Item onClick={onBulkRejectDirectInvites}>
                        <Menu.ItemText>
                            {value.state === 'pending' ? 'Reject' : 'Cancel'}
                        </Menu.ItemText>
                    </Menu.Item>
                </Menu>
            </Flexer>
        </DropMenuButton>
    </Flexer>;
};

const disabledStates = ['used', 'expired', 'rejected'];

const columnHelper = createColumnHelper<DirectInvitationData>();

const columns = [
    columnHelper.display({
        id: 'select',
        size: remInPx(5),
        header: ({ table }) => (
            <Checkbox
                checked={
                    table.getIsAllPageRowsSelected()
                }
                onChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            />
        ),
        cell: ({ row }) => (
            disabledStates.includes(row.original.state) ?
                null :
                <Checkbox
                    checked={row.getIsSelected()}
                />
        ),
        enableSorting: false,
        enableHiding: false,
    }),
    columnHelper.accessor(row => row.state, {
        id: 'state',
        size: remInPx(12),
        header: () => <span>State</span>,
        cell: info => <InviteStateCell value={info.getValue()} />
    }),
    columnHelper.accessor(row => row.email, {
        id: 'email',
        size: remInPx(18),
        header: () => <span>Email</span>,
        cell: info => <TextCell value={info.getValue()} />
    }),
    columnHelper.accessor(row => row.sender, {
        id: 'sender',
        size: remInPx(20),
        header: () => <span>Sender</span>,
        cell: info => <UserInfoButton value={info.getValue()} />,
    }),
    columnHelper.accessor(row => row.workspace, {
        id: 'workspace',
        size: remInPx(12),
        header: () => <span>Team</span>,
        cell: info => info.getValue() ? <WorkspaceInfoButton workspace={info.getValue()} /> : null
    }),
    columnHelper.accessor(row => row.createdAt, {
        id: 'createdAt',
        size: remInPx(12),
        header: () => <span>Created</span>,
        cell: info => {
            return <Tip rest={50} text={info.getValue()?.toLocaleString()}>
                <Copy size="0.9375" flush>{formatDistanceToNow(info.getValue())} ago</Copy>
            </Tip>;
        }
    }),
    columnHelper.accessor(row => row.reviewer, {
        id: 'reviewer',
        size: remInPx(20),
        header: () => <span>Reviewer</span>,
        cell: info => info.getValue() ? <UserInfoButton value={info.getValue()} /> : null,
    }),
    columnHelper.accessor(row => ({ expiresAt: row.expiresAt, state: row.state }), {
        id: 'expiresAt',
        size: remInPx(12),
        header: () => <span>Expires In</span>,
        cell: info => {
            return <Tip rest={50} text={info.getValue().expiresAt?.toLocaleString()}>
                <Copy size="0.9375" flush>{info.getValue().state === 'sent' ? formatDistanceToNow(info.getValue().expiresAt) : null}</Copy>
            </Tip>;
        }
    }),
    columnHelper.accessor(row => row, {
        id: 'actions',
        size: remInPx(3),
        header: () => <span></span>,
        cell: info => {
            return disabledStates.includes(info.getValue().state) ? null : <ActionsCell value={info.getValue()} />;
        },
        enableSorting: false,
        enableHiding: false,
    }),
    columnHelper.display({
        id: 'null',
        header: () => null,
        cell: () => null,
        enableSorting: false,
        enableHiding: false,
    }),
];

const useInvitesPagedQuery = (search: string, sort: string, startAt: number) => {

    const api = useResolve(ApiClient);

    return useQuery({
        queryKey: ['admin.directInvites.list', search, sort, startAt],
        queryFn: async () => api.exec(v2.admin.directInvites.list(search, sort, startAt, 50)),
    });
};

const useSelection = <T extends { id: string, state: string }>(table: Table<T>) => {
    const { rows } = table.getRowModel();
    return rows
        .filter(x => x.getIsSelected())
        .filter(x => !disabledStates.includes(x.original.state))
        .map(x => x.original);
};

const BulkActionMenu = ({ selection }: { selection: DirectInvitationData[] }) => {

    const [open, setOpen] = useState(false);
    const setClosed = useCallback(() => setOpen(false), []);

    const inviteIds = useMemo(() => selection.map(x => x.id), [selection]);
    const onBulkApproveDirectInvites = useOnBulkApproveDirectInvites(inviteIds, setClosed);
    const onBulkRejectDirectInvites = useOnBulkRejectDirectInvites(inviteIds, setClosed);

    const label = !!selection.length
        ? `Bulk Actions (${selection.length})`
        : 'Bulk Actions';

    return <MenuButton disabled={!selection.length} label={label} {...{ open, setOpen }}>
        <Menu>
            <Menu.Section first>
                <Menu.Item onClick={onBulkApproveDirectInvites}>
                    <Menu.ItemText>
                        Approve
                    </Menu.ItemText>
                </Menu.Item>
                <Menu.Item onClick={onBulkRejectDirectInvites}>
                    <Menu.ItemText>
                        Reject
                    </Menu.ItemText>
                </Menu.Item>
            </Menu.Section>
        </Menu>
    </MenuButton>;
};

export const PendingInviteCount = React.memo(() => {
    const api = useResolve(ApiClient);

    const { data, isLoading } = useQuery({
        queryKey: ['admin.directInvites.count'],
        queryFn: async () => api.exec(v2.admin.directInvites.count()),
    });

    if (isLoading || data === 0) return null;

    return <PendingBlock>
        <div>
            {data}
        </div>
    </PendingBlock>;
});

const ApprovalTip = React.memo(() => {
    const [show, setShow] = useState(false);

    return <div style={{ position: 'relative' }}>
        <ApprovalTipIndicator onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
            <div>?</div>
        </ApprovalTipIndicator>
        {show && <ApprovalTipText>
            <div>An Admin must approve invites sent by members.</div>
            <div>Invites sent by admins do not require approval.</div>
        </ApprovalTipText>}
    </div>;
});

export const ApprovalSettings = React.memo(() => {
    const api = useResolve(ApiClient);
    const events = useResolve(AppEvents);
    const account = useObserver(useResolve(AccountModel));
    const [value, setValue] = useState(false);

    const mutation = useMutation({
        mutationFn(next: boolean) {
            return api.exec(v2.admin.accountSettings.save({ approveInvites: next }));
        },
        onSuccess() {
            events.push('account:patch', { account: null });
        }
    });

    useEffect(() => {
        setValue(account?.metadata?.approveInvites ?? false);
    }, [account]);

    const onToggle = (next: boolean) => {
        mutation.mutate(next);
        setValue(next);
    };

    return <DisableFrame disabled={mutation.isLoading}>
        <StatsGroup>
            <Toggle value={value} onAction={onToggle} />
            <ToggleLabel>Require admin approval for invites</ToggleLabel>
            <ApprovalTip />
        </StatsGroup>
    </DisableFrame>;
});

export const InviteBlock = React.memo(() => {
    const popups = useResolve(PopupManager);
    const { workspaces } = useObserver(useResolve(AccountModel));

    const onSelectWorkspace = useCallback((workspace: string) => {
        const ws = workspaces?.find(x => x.id === workspace) as EntityWithLabel;
        if (ws) {
            setTimeout(() => {
                popups.invoke(memberInvitePopup(ws));
            }, 100);
        }
    }, [workspaces, popups]);

    const onAction = useCallback(() => {
        popups.invoke({ ui: WorkspaceSelectPopup, args: { setWorkspace: onSelectWorkspace } });
    }, [onSelectWorkspace, popups]);

    return <StatsGroup>
        <UserStatsView />
        <StatsGroupDivider />
        <InviteButton onClick={onAction}>
            <InviteIcon src="deqo://icons/dash/members-add" />
        </InviteButton>
    </StatsGroup>;
});

export const PeopleInvitesListView = ({ viewOptions, view, setView }: TabProps) => {

    const [search, setSearch] = useState('');
    const [sort, setSort] = useState('createdAt:desc');
    const [startAt, setStartAt] = useState(0);
    const { data, isLoading } = useInvitesPagedQuery(search, sort, startAt);

    const items = data?.items ?? [];

    const table = useReactTable({
        columns,
        data: items,
        getCoreRowModel: getCoreRowModel(),
        enableRowSelection: true,
        manualSorting: true,
        onStateChange(updater) {
            if (updater instanceof Function) {
                const { sorting } = updater(table.getState());
                if (sorting.length) {
                    setSort(`${sorting[0].id}:${sorting[0].desc ? 'desc' : 'asc'}`);
                }
            }
        },
    });

    useEffect(() => {
        table.toggleAllRowsSelected(false);
    }, [data]);

    const selection = useSelection(table);

    return <>
        <Flexer flex="row" gap="1" align="center" padding="1 0">
            <Flexer shrink>
                <BulkActionMenu selection={selection} />
            </Flexer>
            <InviteBlock />
            <ApprovalSettings />
        </Flexer>
        <Flexer flex="row" gap="1" align="center" justify="space-between" padding="1 0">
            <Flexer flex="row" gap="1">
                <Flexer width="16">
                    <Input placeholder="Search" value={search} onChange={setSearch} />
                </Flexer>
                <div style={{ position: 'relative' }}>
                    <ButtonToggle options={viewOptions} selected={view} onChange={setView} />
                    <PendingInviteCount />
                </div>
            </Flexer>
            <Flexer shrink>
                <PageButtonStrip data={data} onPageChange={setStartAt} />
            </Flexer>
        </Flexer>
        <LoadingGuard loading={isLoading}>
            {data && <DataTable table={table} selectFromFirstCellOnly />}
            {data?.items.length === 0 && <Flexer flex="row" gap="1" align="center" justify="center" padding="3 0">
                <Title size="lg" weight={500} color={Colors.textMenu} flush>No invites have been sent yet!</Title>
            </Flexer>}
        </LoadingGuard>
    </>;
};
